import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { anotacaoService } from '../../shared/services';
import type { Anotacao } from '../../shared/types';
import { FileText, Trash2, Edit2, Save, X, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

export function NotesPage() {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const { data: anotacoes, isLoading } = useQuery({
    queryKey: ['anotacoes'],
    queryFn: () => anotacaoService.getMinhasAnotacoes(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => anotacaoService.deleteAnotacao(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['anotacoes'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, texto }: { id: string; texto: string }) =>
      anotacaoService.updateAnotacao(id, texto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['anotacoes'] });
      setEditingId(null);
      setEditText('');
    },
  });

  const handleEdit = (anotacao: Anotacao) => {
    setEditingId(anotacao.id);
    setEditText(anotacao.texto);
  };

  const handleSave = (id: string) => {
    updateMutation.mutate({ id, texto: editText });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditText('');
  };

  const formatTimestamp = (seconds?: number) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getAulaTitulo = (anotacao: Anotacao) => anotacao.aulaTitulo || 'Aula';

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Minhas Anotações</h1>
          <p className="text-gray-600 mt-2">
            Todas as suas anotações das aulas em um só lugar
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : anotacoes && anotacoes.length > 0 ? (
          <div className="space-y-4">
            {anotacoes.map((anotacao) => (
              <div key={anotacao.id} className="card">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="text-yellow-600" size={20} />
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Cabeçalho */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {anotacao.aulaId ? (
                            <Link to={`/aulas/${anotacao.aulaId}`} className="hover:text-primary-600">
                              {getAulaTitulo(anotacao)}
                            </Link>
                          ) : (
                            getAulaTitulo(anotacao)
                          )}
                        </h3>
                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                          <span>
                            {new Date(anotacao.createdAt).toLocaleDateString('pt-BR')}
                          </span>
                          {anotacao.timestampSeg && (
                            <span className="flex items-center gap-1">
                              <Clock size={12} />
                              {formatTimestamp(anotacao.timestampSeg)}
                            </span>
                          )}
                        </div>
                      </div>

                      {editingId !== anotacao.id && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(anotacao)}
                            className="text-gray-400 hover:text-primary-600 transition-colors"
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => deleteMutation.mutate(anotacao.id)}
                            className="text-gray-400 hover:text-red-600 transition-colors"
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Conteúdo */}
                    {editingId === anotacao.id ? (
                      <div>
                        <textarea
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          rows={3}
                          autoFocus
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={() => handleSave(anotacao.id)}
                            className="flex items-center gap-1 px-3 py-1 bg-primary-600 text-white rounded text-sm hover:bg-primary-700"
                          >
                            <Save size={14} />
                            Salvar
                          </button>
                          <button
                            onClick={handleCancel}
                            className="flex items-center gap-1 px-3 py-1 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
                          >
                            <X size={14} />
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-700 whitespace-pre-wrap">{anotacao.texto}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card text-center py-12">
            <FileText className="mx-auto text-gray-400 mb-4" size={64} />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nenhuma anotação ainda
            </h3>
            <p className="text-gray-600 mb-6">
              Faça anotações durante as aulas para revisar depois
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
